import React, { useEffect, useRef } from "react";
import { APP_ROUTES, endpoints } from "config";
import * as Yup from "yup";
import { accessRightsRenderer } from "components/AccessRights/AccessRights";
import { WorkflowFields } from "./models";
import { FieldRenderers, FormField } from "@avamae/formbuilder";
import { Data } from "./Workflows";
import Entity from "components/Entity";
import { phoneNumberFieldRenderer } from "components/helpers/FieldRenderers/PhoneNumberFieldRenderer";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { booleanV2Renderer } from "components/BooleanV2/BooleanV2";
import {
  searchListFieldRenderer,
  searchListReadOnlyFieldRenderer,
} from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/SearchListRenderer";
import {
  actionLinkTextConfig,
  endpointConfig,
  linkConfig,
  nestedEndpointConfig,
  onActionLinkClick,
  onLinkClick,
} from "./workflowsConfig";
import { editSingleSelectRadio } from "components/helpers/CustomFieldRenderers/SingleSelectRadioWithConditional.tsx/SingleMultiSelectFieldRenderer";
import mapFieldRenderer from "components/Maps/mapFieldRenderer";
import { stringV2Renderer } from "components/helpers/CustomFieldRenderers/CustomString/StringV2";
import {
  SingleSelectOption,
  singleMultiSelectFieldRenderer,
  singleMultiSelectReadOnlyFieldRenderer,
} from "components/helpers/CustomFieldRenderers/SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import { dateTimeV2Renderer } from "components/DateTimeFieldV2/DateTimeFieldV2";
import {
  AxiosRequestConfigOrCallback,
  CreatedDataSearchStringFieldNameData,
  FormFieldRendererOverride,
  GroupColumns,
} from "components/Entity/Entity";
import { stringReadOnlyEditRenderer } from "components/helpers/CustomFieldRenderers/CustomString/StringReadOnlyEditFieldRenderer";
import { CrudOptions } from "components/Category/CrudMenu";
import { workflowProgressFieldRenderer } from "components/helpers/CustomFieldRenderers/WorkflowProgressFieldRenderer/WorkflowProgressFieldRenderer";
import { FormikContextType, useField, useFormikContext } from "formik";
import instance from "api";
import { toast } from "react-toastify";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { SingleSelectSearchListWithLinks } from "components/helpers/CustomFieldRenderers/SearchListFieldRenderer/MultiSelectSearchListWithLinks";
import { pascalCaseToCamelCase } from "helpers/pascalCaseToCamelCase";
import { A } from "index";
import { FileInformation } from "components/helpers/CustomFieldRenderers/FileInformation/FileInformation";
import {
  requiredId,
  optionalId,
  optionalString,
  optionalMixed,
  createParentTest,
} from "helpers/validation";

const validationSchema = Yup.object({
  workflowsID: requiredId("Please select a workflow"),
  reasonCodesID: Yup.number()
    .nullable()
    .test(
      createParentTest<number | null>(
        "reasonCodeRequired",
        "Please provide a reason code",
        function (item) {
          let reasonCodeValid = true;

          if (this.parent.workflowsID && this.parent.listWorkflowsDetails) {
            const match = this.parent.listWorkflowsDetails.filter(
              (item: any) => item.workflowsId === this.parent.workflowsID
            );

            if (match && match.length === 1) {
              if (match[0].bReasonCode && !this.parent.reasonCodesID) {
                reasonCodeValid = false;
              }
            }
          }

          return reasonCodeValid;
        }
      )
    ),
  projectsID: requiredId("Please select a project"),
  message: Yup.string()
    .nullable()
    .test(
      createParentTest<string | null>(
        "messageRequired",
        "Please provide a message",
        function (item) {
          let messageValid = true;

          if (this.parent.workflowsID && this.parent.listWorkflowsDetails) {
            const match = this.parent.listWorkflowsDetails.filter(
              (item: any) => item.workflowsId === this.parent.workflowsID
            );

            if (match && match.length === 1) {
              if (match[0].bMessageText && !this.parent.message) {
                messageValid = false;
              }
            }
          }

          return messageValid;
        }
      )
    ),

  listWorkflowsDetails: optionalMixed(),
});

const nestedValidationSchema = Yup.object({
  workflowsID: requiredId("Please select a workflow"),
  reasonCodesID: Yup.number()
    .nullable()
    .test(
      createParentTest<number | null>(
        "reasonCodeRequired",
        "Please provide a reason code",
        function (item) {
          let reasonCodeValid = true;

          if (this.parent.workflowsID && this.parent.listWorkflowsDetails) {
            const match = this.parent.listWorkflowsDetails.filter(
              (item: any) => item.workflowsId === this.parent.workflowsID
            );

            if (match && match.length === 1) {
              if (match[0].bReasonCode && !this.parent.reasonCodesID) {
                reasonCodeValid = false;
              }
            }
          }

          return reasonCodeValid;
        }
      )
    ),
  projectsID: optionalId(), // We automatically send back whatever the GET endpoint gives us initially
  message: Yup.string()
    .nullable()
    .test(
      createParentTest<string | null>(
        "messageRequired",
        "Please provide a message",
        function (item) {
          let messageValid = true;

          if (this.parent.workflowsID && this.parent.listWorkflowsDetails) {
            const match = this.parent.listWorkflowsDetails.filter(
              (item: any) => item.workflowsId === this.parent.workflowsID
            );

            if (match && match.length === 1) {
              if (match[0].bMessageText && !this.parent.message) {
                messageValid = false;
              }
            }
          }

          return messageValid;
        }
      )
    ),

  listWorkflowsDetails: optionalMixed(),
});

type Props = {
  actionType: "create" | "edit" | "view";
  selectedData: Data[];
  onDone(): void;
  idFromRowOption: React.ReactText | null;
  submitButtonText?: string;
  hideActionButton?: boolean;
  nestedListPageUrl?: string;
  parentSelectedId?: number;
  nestedFormGroupColumns?: GroupColumns;
  createdDataSearchStringFieldName?: CreatedDataSearchStringFieldNameData;
  createdDataSearchIdFieldName?: CreatedDataSearchStringFieldNameData;
  nestedFormModuleName?: APP_ROUTES;
  autoMenuActionsCode?: CrudOptions;
  clickedIdIfMultiIds?: string | number;
  nestedFormDataComponent?: React.ReactNode;
};

const createFetchConfig = (parentSelectedId?: number) => {
  return {
    params: {
      Id: parentSelectedId ?? undefined,
    },
  };
};

const fetchConfig = (
  selectedRows: Data[],
  idFromRowOption?: React.ReactText | null,
  parentSelectedId?: number,
  clickedIdIfMultiIds?: number | string
) => {
  const workflowsId = selectedRows.find(
    (r) =>
      String(r.workflowInstances__ID) === String(clickedIdIfMultiIds) ||
      Number(r.workflowInstances__ID) === Number(clickedIdIfMultiIds)
  )?.workflowInstances__ID;
  return {
    params: {
      Id:
        idFromRowOption ??
        (clickedIdIfMultiIds
          ? (workflowsId ?? selectedRows[0]?.workflowInstances__ID)
          : selectedRows[0]?.workflowInstances__ID),
      ProjectsId: parentSelectedId ?? undefined,
    },
  };
};

const overrideOptions = {
  projectsID: (formik: FormikContextType<any>): [SingleSelectOption[], string?, string?] => {
    let options: SingleSelectOption[] = [];

    if (
      formik.values &&
      formik.values.workflowsID &&
      formik.values.listWorkflowsDetails &&
      formik.values.listProjects &&
      formik.values.listProjects.length > 0
    ) {
      const match = formik.values.listWorkflowsDetails.filter(
        (item: any) => item.workflowsId === formik.values.workflowsID
      );

      if (Array.isArray(match) && match.length === 1 && match[0].projectsIDs) {
        const projectIds = match[0].projectsIDs;

        if (Array.isArray(projectIds) && projectIds.length > 0) {
          const finalOptions = formik.values.listProjects.filter((item: any) =>
            projectIds.includes(item.value)
          );
          options = finalOptions;
        }
      }
    }

    return [options, "Project", "Workflow"];
  },
  reasonCodesID: (formik: FormikContextType<any>): [SingleSelectOption[], string?, string?] => {
    let options: SingleSelectOption[] = [];

    if (
      formik.values &&
      formik.values.workflowsID &&
      formik.values.listWorkflowsDetails &&
      formik.values.listReasonCodes &&
      formik.values.listReasonCodes.length > 0
    ) {
      const match = formik.values.listWorkflowsDetails.filter(
        (item: any) => item.workflowsId === formik.values.workflowsID
      );
      console.log(match, "match");
      if (Array.isArray(match) && match.length === 1 && match[0].reasonCodesIDs) {
        const reasonCodeIds = match[0].reasonCodesIDs;
        if (Array.isArray(reasonCodeIds) && reasonCodeIds.length > 0) {
          const finalOptions = formik.values.listReasonCodes.filter((item: any) =>
            reasonCodeIds.includes(item.value)
          );
          options = finalOptions;
        }
      }
    }
    return [options, "Reason Code", "Workflow"];
  },
};

export const workflowsRightsRenderer = (): FieldRenderers<WorkflowFields> => ({
  string: stringV2Renderer(false, null, null),
  accessrights: accessRightsRenderer(false),
  phonenumber: phoneNumberFieldRenderer(false, false),
  boolean: booleanV2Renderer(false),
  address: mapFieldRenderer(false, true),
  singleselect: singleMultiSelectFieldRenderer("singleselect"),
  multiselect: singleMultiSelectFieldRenderer("multiselect"),
  singleselectradio: editSingleSelectRadio(),
  singleselectsearchlistwithlinks: searchListFieldRenderer(
    "singleSelectWithLinks",
    {
      searchEndpoint: endpointConfig,
      linkTextConfig: linkConfig,
      onLinkClick: onLinkClick,
      actionLinkConfig: actionLinkTextConfig,
      handleActionLinkClicked: onActionLinkClick,
    },
    undefined,
    undefined,
    undefined,
    undefined,
    {
      workflowsID: APP_ROUTES.workflowconfiguration,
      projectsID: APP_ROUTES.projects,
    },
    overrideOptions
  ),
  multiselectsearchlistwithlinks: searchListFieldRenderer(
    "multiselectWithLinks",
    {
      searchEndpoint: nestedEndpointConfig,
      linkTextConfig: linkConfig,
      onLinkClick: onLinkClick,
      actionLinkConfig: actionLinkTextConfig,
      handleActionLinkClicked: onActionLinkClick,
    },
    undefined,
    undefined,
    undefined,
    undefined,
    {
      workflowsID: APP_ROUTES.workflowconfiguration,
      projectsID: APP_ROUTES.projects,
    }
  ),
  datetime: dateTimeV2Renderer(false),
  stringreadonly: stringReadOnlyEditRenderer(null, null, false),
  integerreadonly: stringReadOnlyEditRenderer(null, null, true),
  workflowprogress: workflowProgressFieldRenderer(false, undefined, undefined, "communication"),
});

export const workflowsReadOnlyRightsRenderer = (): FieldRenderers<WorkflowFields> => ({
  accessrights: accessRightsRenderer(true),
  phonenumber: phoneNumberFieldRenderer(true),
  boolean: booleanV2Renderer(true),
  address: mapFieldRenderer(true),
  multiselect: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  multiselectsearchlist: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  singleselectsearchlist: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  datetime: dateTimeV2Renderer(true),
  workflowprogress: workflowProgressFieldRenderer(true, undefined, undefined, "communication"),
  document: (_key, field) => <FileInformation field={field} />,
});

const nestedRightsRenderer = (): FieldRenderers<WorkflowFields> => ({
  string: stringV2Renderer(false, null, null),
  accessrights: accessRightsRenderer(false),
  phonenumber: phoneNumberFieldRenderer(false, false),
  boolean: booleanV2Renderer(false),
  address: mapFieldRenderer(false, true),
  multiselect: singleMultiSelectFieldRenderer("multiselect"),
  singleselect: singleMultiSelectFieldRenderer("singleselect"),
  singleselectradio: editSingleSelectRadio(),
  singleselectsearchlistwithlinks: searchListFieldRenderer(
    "singleSelectWithLinks",
    {
      searchEndpoint: nestedEndpointConfig,
      linkTextConfig: linkConfig,
      onLinkClick: onLinkClick,
      actionLinkConfig: actionLinkTextConfig,
      handleActionLinkClicked: onActionLinkClick,
    },
    undefined,
    undefined,
    undefined,
    undefined,
    {
      workflowsID: APP_ROUTES.workflowconfiguration,
      projectsID: APP_ROUTES.projects,
    },
    overrideOptions
  ),
  multiselectsearchlistwithlinks: searchListFieldRenderer(
    "multiselectWithLinks",
    {
      searchEndpoint: nestedEndpointConfig,
      linkTextConfig: linkConfig,
      onLinkClick: onLinkClick,
      actionLinkConfig: actionLinkTextConfig,
      handleActionLinkClicked: onActionLinkClick,
    },
    undefined,
    undefined,
    undefined,
    undefined,
    {
      workflowsID: APP_ROUTES.workflowconfiguration,
      projectsID: APP_ROUTES.projects,
    }
  ),
  datetime: dateTimeV2Renderer(false),
  stringreadonly: stringReadOnlyEditRenderer(null, null, false),
  integerreadonly: stringReadOnlyEditRenderer(null, null, true),
  workflowprogress: workflowProgressFieldRenderer(false, undefined, undefined, "communication"),
});

const nestedReadOnlyRightsRenderer = (): FieldRenderers<WorkflowFields> => ({
  accessrights: accessRightsRenderer(true),
  phonenumber: phoneNumberFieldRenderer(true),
  boolean: booleanV2Renderer(true),
  address: mapFieldRenderer(true),
  multiselect: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  multiselectsearchlist: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  singleselectsearchlist: singleMultiSelectReadOnlyFieldRenderer("multiselect"),
  datetime: dateTimeV2Renderer(true),
  workflowprogress: workflowProgressFieldRenderer(true, undefined, undefined, "communication"),
  document: (_key, field) => <FileInformation field={field} />,
});

function Workflow(props: Props): JSX.Element {
  const {
    actionType,
    selectedData,
    idFromRowOption,
    submitButtonText,
    hideActionButton = false,
    nestedListPageUrl,
    parentSelectedId,
    nestedFormGroupColumns,
    createdDataSearchStringFieldName,
    createdDataSearchIdFieldName,
    nestedFormModuleName,
    autoMenuActionsCode,
    clickedIdIfMultiIds,
    nestedFormDataComponent,
  } = props;

  let initialDataUrl = endpoints.workflows.view;

  if (actionType === "create") {
    initialDataUrl = endpoints.workflows.create;
  }

  if (nestedListPageUrl) {
    initialDataUrl = nestedListPageUrl;
  }

  let finalFetchConfig: AxiosRequestConfigOrCallback<Data> | undefined = undefined;
  if (parentSelectedId) {
    if (actionType === "create") {
      finalFetchConfig = createFetchConfig(parentSelectedId);
    } else {
      finalFetchConfig = fetchConfig(
        selectedData,
        idFromRowOption,
        parentSelectedId,
        clickedIdIfMultiIds
      );
    }
  } else {
    if (actionType === "create") {
      finalFetchConfig = undefined;
    } else {
      finalFetchConfig = fetchConfig(selectedData, idFromRowOption, undefined, clickedIdIfMultiIds);
    }
  }
  if (!parentSelectedId) {
    return (
      <Entity
        initialDataUrl={initialDataUrl}
        readOnly={actionType === "view"}
        submitUrl={initialDataUrl}
        onDone={props.onDone}
        selectedData={selectedData}
        idFromRowOption={idFromRowOption}
        formType="json"
        fieldRenderers={workflowsRightsRenderer}
        validationSchema={validationSchema}
        readOnlyFieldRenderers={workflowsReadOnlyRightsRenderer}
        nullStringValues={[]}
        readOnlyFields={
          actionType === "create" || actionType === "edit"
            ? [
                {
                  fieldName: "Id",
                  readOnlyRenderer: "integerreadonly",
                },
              ]
            : undefined
        }
        fetchConfig={finalFetchConfig}
        submitButtonText={submitButtonText}
        hideActionButton={hideActionButton}
        hiddenFields={
          actionType === "view" ? ["bExistingUser", "workflowName", "communication"] : []
        }
        hiddenGroups={actionType === "view" ? ["Workflow Name", "Trigger Communication"] : []}
        conditionalFields={
          {
            ProjectsID: {
              dependentField: "workflowsID",
              dependentValues: null,
              dependentType: "number",
            },
            ReasonCodesID: {
              dependentField: "workflowsID",
              dependentValues: null,
              dependentType: "number",
            },
          }

          // When "Other user" is ticked, the companies multi-select should appear (and is mandatory), and it doesn't appear when "Principle user" is ticked
        }
        // If there is a related origin form we've been redirected from then we will set the values for these fields to the values state here and make them readOnly
        relatedFormDataCreateInfo={
          nestedFormModuleName
            ? [
                // {
                //   fieldName: "workflowAccessTypesId",
                //   value: 1001,
                //   label: "Site",
                //   isMulti: false,
                // },
                // {
                //   fieldName: "sitesID",
                //   value: null,
                //   label: "Site",
                //   isMulti: false,
                // },
              ]
            : undefined
        }
        // If the related origin form is the /sites create or delete then we will save the submit response IDs as Ids for the workflowsID field back on the sites page. We will also set that the dataSource fieldName is workflows, which is what will use the ids we're saving as workflowsID here
        relatedFormOriginFormAutoSelectAllValuesFields={{
          "/sites": [{ fieldName: "workflowsID", dataSourceFieldName: "workflows" }],
        }}
        // Extra data to display on each row automatically selected in the orgin form
        // First key is origin module name, second key is related form module name and the last objects are the fieldName of the value you want to show, and dataSource gets the label for the value you want to show to send back to the origin form to display
        relatedFormExtraDataToDisplayFields={{
          "/sites": {
            "/workflows": [{ data: "workflowsID", dataSource: "listWorkflowTypes" }],
          },
        }}
        groupColumns={
          actionType === "view" ? { workflowprogress: 2, triggercommunication: 2 } : undefined
        }
        createdDataSearchStringFieldName={createdDataSearchStringFieldName}
        createdDataSearchIdFieldName={createdDataSearchIdFieldName}
        nestedFormModuleName={nestedFormModuleName}
        autoMenuActionsCode={
          actionType.toLowerCase() === autoMenuActionsCode?.toLowerCase()
            ? autoMenuActionsCode
            : undefined
        }
        nestedFormDataComponent={nestedFormDataComponent}
        clickedIdIfMultiIds={clickedIdIfMultiIds}
        fieldsInBoxes={actionType === "view"}
        hideGroupLabels={actionType === "view" ? ["Details", "Document"] : undefined}
      />
    );
  } else {
    return (
      <Entity
        initialDataUrl={initialDataUrl}
        readOnly={actionType === "view"}
        submitUrl={initialDataUrl}
        onDone={props.onDone}
        selectedData={selectedData}
        idFromRowOption={idFromRowOption}
        formType="json"
        fieldRenderers={nestedRightsRenderer}
        validationSchema={nestedValidationSchema}
        readOnlyFieldRenderers={nestedReadOnlyRightsRenderer}
        nullStringValues={[]}
        readOnlyFields={
          actionType === "create" || actionType === "edit"
            ? [
                {
                  fieldName: "Id",
                  readOnlyRenderer: "integerreadonly",
                },
              ]
            : undefined
        }
        fetchConfig={finalFetchConfig}
        submitButtonText={submitButtonText}
        hideActionButton={hideActionButton}
        hiddenFields={
          actionType === "view" ? ["bExistingUser", "workflowName", "communication"] : []
        }
        hiddenGroups={actionType === "view" ? ["Workflow Name", "Trigger Communication"] : []}
        conditionalFields={
          // When "Other user" is ticked, the companies multi-select should appear (and is mandatory), and it doesn't appear when "Principle user" is ticked
          {
            ReasonCodesID: {
              dependentField: "workflowsID",
              dependentValues: null,
              dependentType: "number",
            },
          }
        }
        // If there is a related origin form we've been redirected from then we will set the values for these fields to the values state here and make them readOnly
        relatedFormDataCreateInfo={[]}
        // If the related origin form is the /sites create or delete then we will save the submit response IDs as Ids for the workflowsID field back on the sites page. We will also set that the dataSource fieldName is workflows, which is what will use the ids we're saving as workflowsID here
        relatedFormOriginFormAutoSelectAllValuesFields={{
          "/sites": [{ fieldName: "workflowsID", dataSourceFieldName: "workflows" }],
        }}
        // Extra data to display on each row automatically selected in the orgin form
        // First key is origin module name, second key is related form module name and the last objects are the fieldName of the value you want to show, and dataSource gets the label for the value you want to show to send back to the origin form to display
        relatedFormExtraDataToDisplayFields={{
          "/sites": {
            "/workflows": [{ data: "workflowTypesId", dataSource: "listWorkflowTypes" }],
          },
        }}
        // When there are no related/origin forms then send this value in the payload:
        extraPayloadItems={
          actionType === "create" || actionType === "edit" ? { bCompleted: 1 } : undefined
        }
        overrideParamIds={
          parentSelectedId && (actionType === "create" || actionType === "edit")
            ? [
                {
                  idParamOverrideName: "projectsID",
                  idParamOverrideValue: parentSelectedId,
                },
              ]
            : undefined
        }
        overrideFormFieldRenderer={
          parentSelectedId && actionType === "create"
            ? {
                projectsID: searchListFieldRenderer(
                  "singleSelectWithLinks",
                  undefined,
                  undefined,
                  true
                ),
              }
            : parentSelectedId && actionType === "edit"
              ? {
                  projectsID: searchListFieldRenderer(
                    "singleSelectWithLinks",
                    undefined,
                    undefined,
                    true
                  ),
                }
              : undefined
        }
        groupColumns={
          actionType === "view" ? { workflowprogress: 2, triggercommunication: 2 } : undefined
        }
        createdDataSearchStringFieldName={createdDataSearchStringFieldName}
        createdDataSearchIdFieldName={createdDataSearchIdFieldName}
        nestedFormModuleName={nestedFormModuleName}
        nestedFormDataComponent={nestedFormDataComponent}
        autoMenuActionsCode={
          actionType.toLowerCase() === autoMenuActionsCode?.toLowerCase()
            ? autoMenuActionsCode
            : undefined
        }
        clickedIdIfMultiIds={clickedIdIfMultiIds}
        fieldsInBoxes={actionType === "view"}
        hideGroupLabels={actionType === "view" ? ["Details", "Document"] : undefined}
      />
    );
  }
}

export default Workflow;
